// React bindings pra capabilities. Subpath import: '@app-iptv/device/react'.
//
// `useCapability(key)` re-renderiza quando capability muda — útil pra
// rede oscilar (cliente em movimento) ou viewport resize (raro em TV
// mas comum em web responsivo).
//
// Module-level subscribers — rebind em refresh. useSyncExternalStore
// resolve hydration mismatch + concurrent mode safe.

import { useSyncExternalStore } from 'react'

import {
  getCapabilities,
  getCapability,
  subscribeCapabilities,
  type DeviceCapabilities,
} from './capabilities'

// SSR snapshot determinístico — não depende de signals reais (que lêem
// browser APIs como screen, navigator, canvas inexistentes em Node).
// Em hidration, server e client DEVEM produzir mesmo HTML; signals
// reais chegam pós-mount → useSyncExternalStore re-renderiza.
//
// Valores escolhidos pra "default mid TV 1080p" — caso mediano que
// não causa flash visual ao hidrate em maioria dos devices.
const SSR_SNAPSHOT: DeviceCapabilities = Object.freeze({
  animations: 'full',
  autoplayHero: true,
  heroIntervalMs: 6000,
  livePreviewEnabled: true,
  defaultPlayerQuality: 'auto',
  // SSR conservador: native-ts é capacidade de HARDWARE por classe de device —
  // o snapshot determinístico NÃO a assume (fica no mpegts.js). O valor real
  // (webOS gen 4-5) chega pós-mount e o hook re-renderiza.
  nativeTsPlayback: false,
  // Prefetch de filme: desligado por padrão em todas as plataformas (AP-161).
  vodPrefetch: false,
  episodeInitialWindow: 80,
  episodeExpandStep: 160,
  catalogPageSize: 2000,
  liveListOverscan: 1, // default mid: 1 linha de folga (bate com computeCapabilities)
  retryCount: 1,
  retryDelayMs: 1500,
  fetchTimeoutMs: 15000,
  rqGcTimeMs: 5 * 60 * 1000,
  epgRefreshIntervalMs: 30000,
  catalogRefreshIntervalMs: 30 * 60 * 1000,
  swImageMaxEntries: 3000,
  swImageCacheSuffix: 'mid',
  imagePreset: 'medium',
  backdropPreset: 'fhd',
  preferredImageFormat: 'jpeg', // conservador — server desconhece browser features
  prefetchConcurrency: 2,
  longSessionThresholds: {
    warn: 3 * 60 * 60 * 1000,
    degrade: 6 * 60 * 60 * 1000,
    suggest: 8 * 60 * 60 * 1000,
  },
  longSessionMode: 'normal',
  cpuTier: 'mid',
})

/** Hook reativo a capability. Re-renderiza quando muda.
 *
 *  Em TVs (caso comum), capabilities são estáveis durante a sessão —
 *  hook nunca re-renderiza. Custo idêntico a useState normal.
 *
 *  Em web responsivo / viewport mudando, hook reflete em milliseconds.
 *
 *  SSR: retorna snapshot determinístico (mid 1080p). Pós-mount, signals
 *  reais chegam e hook re-renderiza com valor real. */
export function useCapability<K extends keyof DeviceCapabilities>(
  key: K,
): DeviceCapabilities[K] {
  const get = (): DeviceCapabilities[K] => getCapability(key)
  const getSsr = (): DeviceCapabilities[K] => SSR_SNAPSHOT[key]
  return useSyncExternalStore(subscribe, get, getSsr)
}

/** Hook que retorna toda a struct. Use quando precisa múltiplas caps. */
export function useCapabilities(): DeviceCapabilities {
  const getSsr = (): DeviceCapabilities => SSR_SNAPSHOT
  return useSyncExternalStore(subscribe, getCapabilities, getSsr)
}

function subscribe(cb: () => void): () => void {
  return subscribeCapabilities(cb)
}
